import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, NgZone, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';
import { AppState, Selectors } from 'store/root-reducer';

import { of, Observable, Subject, interval, zip, forkJoin } from 'rxjs';
import { switchMap, concatMap, take, map, takeUntil } from 'rxjs/operators';

import { Crp, RecordCrp } from 'modules/shared/models/crp.models';
import { Customer } from 'modules/shared/models/customer.models';
import { CrpResponse, RecordCrpResponse } from 'store/models/crp.models';
import { Edge, NodeParameters, Node, CrpTracker, DrawEvent, RecordCrpTracker } from 'modules/shared/models/relationship-map.models';
import { IBreadcrumbs } from 'modules/shared/components/breadcrumbs/breadcrumbs.component';

import { CustomerService } from 'modules/core/services/customer.service';

import { ThemeService } from 'modules/shared/services/theme.service';

import * as vis from 'vis';
import * as _ from 'lodash';

@Component({
  selector: 'artemis-customer-relationship-map-page',
  templateUrl: './customer-relationship-map.page.html',
  styleUrls: ['./customer-relationship-map.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerRelationshipMapPage implements OnInit, AfterViewInit, OnDestroy {

  private unsubscribe$: Subject<void> = new Subject();
  private currentTheme: string = 'theme_dark';
  private stateConfigs: any = {
    theme_dark: {
      nodes: {
        enable: {
          font: {
            color: '#fff'
          },
          color: {
            background: '#666',
            border: '#fff',
            highlight: {
              border: '#fff'
            }
          }
        },
        disable: {
          font: {
            color: 'rgba(255, 255, 255,0.5)'
          }
        }
      },
      edges: {
        enable: {
          color: {
            color: 'rgba(255, 255, 255, 0.4)',
            highlight: '#ccc'
          }
        },
        disable: {
          color: 'rgba(255, 255, 255, 0.5)'
        }
      }
    },
    theme_light: {
      nodes: {
        enable: {
          font: {
            color: '#000'
          },
          color: {
            background: '#e0e0e0',
            border: '#000',
            highlight: {
              border: '#000'
            }
          }
        },
        disable: {
          font: {
            color: 'rgba(0, 0, 0, 0.5)'
          }
        }
      },
      edges: {
        enable: {
          color: {
            color: 'rgba(0, 0, 0, 0.4)',
            highlight: '#ccc'
          }
        },
        disable: {
          color: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }
  };
  maximized: boolean = false;
  loading: boolean = false;
  network: vis.Network;
  nodes: vis.DataSet<Node>;
  edges: vis.DataSet<Edge>;
  nodeDataArray: Node[] = [];
  linkDataArray: Edge[] = [];
  draw$: Subject<DrawEvent> = new Subject();
  recordTracker: RecordCrpTracker = {};
  customerTracker: CrpTracker = {};
  sharedCrpTracker: CrpTracker = {};
  customerId: number = null;
  loading$: Subject<number> = new Subject();
  defaultCanvasHeight: string = '500px';

  breadcrumbs: Observable<Array<IBreadcrumbs>> = this.store.select(Selectors.customer.getCustomer)
    .pipe(
      takeUntil(this.unsubscribe$),
      map((customer: Customer) => {
        if (customer) {
          return [
            {
              url: '/customers',
              label: 'Customer List'
            }, {
              url: `/customers/${customer.id}`,
              label: customer.name
            }, {
              url: `/customers/${customer.id}/relationship-map`,
              label: 'Relationship Map'
            }
          ];
        } else {
          return [];
        }
      })
    );

  constructor(private customerService: CustomerService,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private themeService: ThemeService) { }

  ngOnInit() {
    this.customerId = this.route.snapshot.params['id'];
    this.network = this.initNetwork();
    this.network.setSize('100%', this.defaultCanvasHeight);
    this.loading = true;
    this.loadNetworkData(this.customerId).subscribe(() => {
      this.network.fit();
      this.loading = false;
      this.cdRef.markForCheck();
    });
    this.draw$.subscribe((data: DrawEvent) => {
      this.edges.add(data.edges);
      this.nodes.add(data.nodes);
      this.network.fit();
    });
  }

  ngAfterViewInit() {
    this.checkCurrentTheme();
  }

  /**
   * @method checkCurrentTheme
   * Check current theme (light or dark)
   */
  private checkCurrentTheme(): void {
    this.themeService
      .getTheme()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (currentTheme: string) => {
          if (currentTheme) {
            this.currentTheme = currentTheme;
            this.network = this.initNetwork();
            this.cdRef.detectChanges();
          }
        }
      );
  }

  /**
   * @method generateNode
   * Generate the map node
   */
  generateNode(nodeData: NodeParameters): Node {
    return <Node>{
      customer: nodeData.customerId,
      id: nodeData.recordId,
      type: nodeData.type,
      shape: 'circularImage',
      label: nodeData.name,
      image: this.generateNodeImage(nodeData.name, nodeData.type, nodeData.disable)
    };
  }

  /**
   * @method generateNodeImage
   * To generate the node image to show on the map
   */
  generateNodeImage(name: string, type: 'CORPORATE' | 'INDIVIDUAL' | 'SELF', disable: boolean = false): string {
    let bgColor = 'linear-gradient(to right, #e5b04a, #e5b556, #e6b962, #e6be6e, #e6c27a)';
    let icon = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAYAAADimHc4AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbW
    FnZVJlYWR5ccllPAAACoNJREFUeNrsXWlsVUUUfrdQAa0FhUJtEWutAmVRgRgXYtxY3KqSCC64AEEFjPuCERs0QY0JKj+UuETEiF
    HEKC4E1KqJaFCkKjSKCLVaWUoLIiiCCvU7ckqGYWbe3Nc3c2/bN8mX9959s3xn5t6ZM2fOzA0SMQ2NjY098HEi0B8oAYqAQqAb0B
    XoICXZDWwBGoD1QA2wFqgCvg2CoC6OcgYxqvACfIwEzgVO5wpPZ6AG+RyoABajQTa0+QZApRfjYzRwOTDIc/GVwOvAfDRGdaKtBF
    R6NjAGqGiMT6hgTtmtueJzgLuA2sb4hlrmmNNquiAI0xEfk4D7efAMGzYDqwDqJn4C6nmg3SbF68IDdB5wLEDd2wCgewpl0mA+A5
    iN7mlXS77rRwE/hbwLVwIzgcuA/DRwyOe8ZnLeYQJxH9USK74XsCiEoMuA24GeHrj15LKWheBHsvRqKZU/AfjDQqjtwJNAaYRcS5
    nDdgu+JNOEOFd8LrDAQpAGYCrQOUbcOzOnBgv+JGNu3Cq/H7AmCfE/gXKfGkaKmlo5czUFkrVfXEiPsHiEXwMKW9AYVsick3WhI6
    ImOg7410ByI1DWgrW4MpZBF0j2cVGRm5LkDnkXyGsFE8g8lsUUpsSp8vcA04Ag0UoCycIy7Ym8Ebjb0YWdLbnLseySdhrkH+eawA
    hDn18PDGkDxsTBLKtuTBjhqmBSNX/XFLwJGNCGLLoDWGaddtQv3QXmGvT8+rZU+VIj1BvmCbnpLGyBoc8fkmijgbsj3ZiwIF2FjD
    doO2WJNh54YNZpRxOamzlZNXdoMp/mWdDerP7OAb6SxqNtwGfAcxynt2du0wwGvF7NyXiRYZLlYzGnC3A3sDqF1a1VbHbO9cAzME
    zWFqWa6SiDeSHPsUAd+K7aloZlxq28zJjtmHOewWyhXdQJNJnRMuJ3iX1Le3K4JAiCtx0KQt4R84A+ir9/AT4GvgfWAf8C7QBaOa
    NHnbQxcmlRmbppWfNqcF/lcjzAx0LFXzVAX+vlTWR0m6YlX3d8F40Fdkll/g28QBqHZR7kdTGcNbe9Ul5/AVc4lmG+pu5us80gR7
    Mw8adLkzLynqQocyFwTDMnj0sU+U5yKEehZj2hwWothPtLVSh3SPoqxV0/OY3538R5iuEah/KUa+rwrmQJ22v8duxaLzWyQ6TKoY
    nNeQ7KGSqprlTmKY5k0vUiVLftTQlHa1puqkOia6XJ3YUO78zTpZlrNXCoo7KmaupyjClRhca41NkRyceksm6xTHcEcC5wLeMcmj
    NYpr1GKvMxR7J11izVVugSFGta7ElHBI+Tup63LNKQdvO+ZupP1xZTw1jkI6737nLl88MuL6pQHOaRKXVE7ilpyn5UEmvsqyEmX/
    OAww35FbBK2hRmOpKx1LpLx8UVKo81h32/qKrNSPIoVyq4kY5fx3b5vYr/vzB1nfhvljRb7uBIVpUH3grVHaEKdzoidYW0ilRgsL
    G8r9AkbgS6CvGO5Gu/SnHf0dmscP14Ke7FjmS9XVO3BTYm56MdkZorlPGhIZ68/rzENNiy8e4TKc1Vhvjik/WUI1l7aup2vBhpni
    JClUN1ULRu3mO4+0UVtQo4zCLvw6X8fzDEfVSIt9yhvCqv7HliBJUL+eOOyGRLffYwg84uhuEhyrhYSjvYwuK702EDPK5yfaf/sn
    g3YpEi3VJHfHpIVth1mnhnCN9pQ90HIcp4L7FvI0dTGKqJt0b43ikd+xE0QVWXRVT3WfgyUJPoc0dkukm/t2riiTfF10EQNNoWgL
    h7KY1wSWfQq5d+d3Eks64uB2axDf0gYhBiUyLacIjw/Y8U0otdSjtNnN3S744uBOG63Kz4awA1QInijyqHFbtd+q3Tv0XCxSmUI9
    712zRxZMPY3w7lVtVpSZam/1/rkMhG6bdO1V0pfB9smimrVD98nCR2YRaNpOqS0hlUY10RNYBqkaXGFQs8jn9Jd3eJJuoS4Y4knm
    G8MMqFgZ7K+0gTr6/w/Xdwc9kAqjotzFIMiglNf5XO8I3w/VRNQ1G3MVe4NBl39pUWd/91+JgoXJqLvLZrop9t8ZSkK2xWKiSSUa
    opXOaSCfK/V7LJ68wFR7GdRrR4PgR00tiXHlEsJHU3zEdE/877Hct8qaKedyU00+SzHJPpI5V3kSHu+QqPbKrYF9mCex/wEvCbYl
    lzuCFfeRn0BMcyn6Wq6EgagAmJiz+r2RVGF/cSy62vTWGHaWWNVsGk2f9HHuSNXQOcJpU5K0n84y03f7+pXPA4MK9ZEcgbrwZgUs
    9I5Z5vkaY/MJ1dASt5HWMhe9L1tkh/tlTmq55k1TaA90FYGjhFi2edbtBMU3m0svaLtBCT70lW5SCcpbHFdPVBCuohmRjIXr+HL1
    Hlz3Ho+DtLmvhN8mhyUan7W6gBGhR/dPdEihrhS3xMFy5dAExxcAeS3+b1wqWXUfZrHm1bqjpt0LmgP+vT6oby2gFLJU+F/mnMv5
    uk8/9q68aSRg7PqlzXszRT5BKf5HAnUhc0VjDUkYHuFZNqGjLMTuxbh2gK43mm7TMcpzRPQMg7FC2zORFBYO9oa9XUMs+rpTyfjk
    i2OkU930F/DNOoovkREX0lrGpqyKtQmiH/GMVpLXxqlyoM+/+A1LC7OhyTJc+Gn5urmvKi/mLJjjQ0Ipl0u416NEXwtihvSfhMyf
    3wvbCqKfsJOfcBteSiXZRviuDVLcWS9MMSn5tDpC2WbEdVrjzfLPkkdUvx6phlSZrMxcvDqqaIQ54enwrp/gFOjlAOK8csr66JIc
    ifIN3JK5OpprytVQwPRCxDctdEjujNOTekADfYqqZsqBM3+S037kjxwz+5cy5H9OqeHlKIN5OpptxlrZC6rD4R8w7lnu51g0YKpo
    QNJtUUvx9MaVuoW972GzQ4gdctSiGFGS7x2q+a8kY/cdmSPKSzIuYbbosSJ/K6SS8FoZ6QVVMalCVvaBL62BhwTWmTnvdtqiGF6i
    jp1NTPvyFxnRgDnqltU+XE3jdqhxSuv+I4g/3dUkw4prZRO0nrOT2qIKSAtyr4bQnjvuiQW/OOKuBMIjmsoxmGtkbXh3CE4Na8wz
    qEvrZak1FZTAQlE28Nc3o+JpzKDC+C6Bg2s8gObArBsVPUky2BS0oHNiXLNNIjy1pKcHJkGWccm0P7Yt4Abg7t48wzx1Ym7/fdHF
    spFJI5uFVdL+4PbuWCMkcXH1wn/o4u5gIzh3cfWPn+Du8WCs4cXx/V8fUCgcwLHKJ6gYNAJPMKk5i/RybzEh9P5DKvsYrqNVbSwJ
    x5kVvEhDOvMowB+czLPGMiTOZ1tjFohMwLnWPSEJlXmicJPt4DQ0txdF4/HYaRyvZX2i5Fb72oBsinno6UoZ2d8h4v2nRHW0Fp7k
    F+QeR9RvapVHZ8bgHoMNnZ1m+9aAFPQw67vNQ2xjfUMsecRGsN7EQ7RuMGGVWoYE7ZvusjiLgxqJsYDVwODPJcfCVAbjbz0c1UR1
    UHQYyeDNqwMBKg4+fpTUhFaS6iJrHv+Ehyjl2MSt8QB7mDGHdVtIPwRIC2JZVwgxTyQEuDubznazcPnjRAr+cKp8MHaa/bt6jwuj
    jK+Z8AAwBJLZ+p+Ld5bAAAAABJRU5ErkJggg==`;

    if (type === 'CORPORATE') {
      bgColor = 'linear-gradient(to right, #268db2, #2d9ab9, #38a8c0, #47b5c5, #57c2ca)';
      icon = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAYAAADimHc4AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbW
      FnZVJlYWR5ccllPAAACH5JREFUeNrsXV1oFUcUnr2NqK2NaTUqjZWYBgpqNFhBEMGCpREKoaYkoY+a2lZ8sFqppQaxoA+CP/HJtt
      D42JrE1oJo+nNFShF9EX/7UK1JG1NTk7QajSgY0+94J+Fme2bv7L2Tvbv3zoGPe9nZmT3znd35nzOOCKkMDw/PxM8iYAFQDpQCJc
      B0YBow0RXlIdAP9AHdQCdwDbgMXHAc5+8w5tMJEeEv4GcVsBJYJgk3KWSQ00AcaIdB/sp7A4D0MvzUAbXA4oAffw5oBVpgjOsiXw
      SkTwDqgfhweCQudZqQy8RPAbYAXcPhlS6p45ScKYKQmUn4WQ9sk5WnX7kFXAKomOgAemVFe9t1X5GsoIuBuQAVbxXAjDSeSZX5Lu
      AgiqcHUX7ra4AOn2/hRWAvsBqYZUCHWTKtvTJtP0K610SR+DnAcR8ZPQNsAmYHoNts+awzPvSjvMyJCvkNwD2NTA0ATcC8LOo6T+
      owoKEv5akhzMQXAm0aGekDPgamhkj3qVKnPg39KY+FYSN/PvBbCsUHge1BtjDSbKltl7p6CeV1fliUrtL4hA8DJRGqw0qkzqmK0K
      psK7oGeOSh5E2gOsKtuGqZB5VQ3tdkS7kNKd6QY0BxDnQgi2VevGRDmMgfAhoBR+SIUF5knoaybgRZ7KjkfpSLHM0i6b5H/teMtw
      JVHmV+L7AkDwYTX5F5VdUJVeP1YGpq3lE8uAeoyKMR3QqZZ1XraL7pBxZ6tPN784l8lxF6PfoJhSYf1uZR5i8ReSqyOFLVCW2mHr
      LWo7VTLfJcZMWsah01ZJo4jWreVSTeKKyM8NToMYA3J5OEj3t0shxL/Zh+gqqzdjzdRGs8hheKLe1sj1k1bFHjN7FJwHVFYtWWbs
      /6QDWzNomL4ygS+gA/+5mgNsdxalMoUYmfyhzl+Dzyfz5F/ltEYpmNWzYhbpOOFacoJiYGdYaUcc+O4dyVHRr5L1HMJ/RxcyExJo
      33Bb96YTcs2G0LGm+RHO1mgqZJbtUGgIUK8LORiUzLNPZZerVln+TMLRslx8ovgGprbmXCHlj2nuVV+ysgrvYwQcTtW8kXClw3vM
      dEugsczFCnt4GeiPFIa5K+yiA+cfYJ8Kzr+rvAYa7yKFNUPE1+nqqohEsj2KQsTacSdqXRpOC0jCuC6hTpfGELlbRFxV0dZwCu7X
      oW5dmvlse06wLi7iwTVDvGAHJzBLc+v9XSmLEcZq4tlpyPVsKrFJFbAihraU3+G5pv1FHNNJeLxErpVEJf+M1xzmKroglPnDePGG
      Alc8MVKNcVwBvyDPCt7leted9OYIXGfauBo+OZOXB4Ay8ELa93zxoS580jdcAyJu4PtvQwJj8x155wHpO7Eblm4i+WN2PCcUnN3J
      n0BSxURDpteTMmKi4XxpiyiaQXZVeP5c1YPUBc3mKCKsgA5UzAZUubceE4LY8pyv9rli/j8jtXD5ABuEmWTsuXceE4LYkpOiy3LF
      /GheN0OnXEnmcC+gNUbAi4YDhNKkKLNO67E2A++5hr08gA3Gz9vwG2EGi+odJwmu+E8Au4zVybGLMlQ3bFGsAawBqAc0bxnKXGuH
      CNgodkgH+42tnyZVy45n5/TNE8mmH5Mi4cp33UDKWVXO4R0dKgtJLL9Y5pNi9f1UyzSbNp24g0gxp25zjtLlB0kcsDfDNIhxWG06
      zUTHN6gPl8iRueiAl+4G2BLTGMC8fpNTLAJSag2IS3KiujReIsRR1wiQxwURFvmaXOmKi4vBiTHmW5emC55c2YcFx2EvcjPWFuzv
      J1y5sxeY25djp5KCLO3ECuCV603GVc/tOSdG7ePZ5sgHZF/DpLYcai2lPXPmoA6cj6nI/IVvSlnrl2bsR5ePIGDVrD6F6gu5RcO4
      7zCulBkVgiaFIaNTtZZ8e5+CGXnEuZoFbuZrtBY2w+gt2gIV24n2TSWRsmH58RMiBxtpYJOpnsLt89IfM5E4H2OK23lPqW9eL/+8
      NIxuyacRvgG+AGEylQl+458PYTV1uYIOL2iNIA+DQe4ecAE5EmaDZbarVls+AntQ5IjpVfAMlngl8XtDVK3m+z+PYTR1uZoH7J7R
      hx7xN+sskYidAOE7ezjqeBJtN9AzzrKUVPUTC6nddMk+YzdIrMDqRpenHWfsmVW3Zqb3bPxF2N32Yowop0PWX4MOopzSTfNNkMTc
      ddTUzxpj1QVCJPWkrWYRNLfrGiFUnyoeoolJjH504tohNMEE0uHLIuy8aQT1wckty45YTkUvgygBRyr8KVW7StdJulflS2CX6r7a
      BgXNRoGwCW+1Pw7mtIPrXuyxLlPnGhCN4oORTpfgFkhGZ35yEp7tf57riVOFDweATcfZkqDd21oTSmcZW5PpnKuHx1XSwSY/qTme
      CrinGg9AwASw6IxJDxABNMw74/5pvzbsqz4Ie8ab/DasmZMPUFkBGuiMQM2RATTJu9T+aL+3qRGDWeyQQTN7WSKy0p8PNwJPw9FF
      iHv82KL+Fn4DufeXoM/GGYpx7NNO/7TPdl4CNFsUOyjjgK4i3wOsLkcQ5PyDyOyjkyuWqAcJCfpGSqY6xy3QDZO8YqSVGdg9yORf
      Agt1RHV2X/ILckhe1RhiFQ3h7mGZLMNGi8QVE8znYw1MfZujJmD3QOiSHskeapOrcBGIGm4miNDI2Zp7P9lbyM0C4eWszUAfSKxM
      5Ot++FItkbp5mpuQCtPqMxm3R2fNIE+i7goGomK4pfA7UwaH1RV4gPaOiSOubuGihy1ArUA/EQER+XOk0Img8ny8YokyOstNRlcc
      CPp+X4tEq5JXmtZl4ZwGUM8qVM7nzJoyxtajM9fNEpEtuCaGdK+8j6/GyLE+KiisbbF4nE/tpyaZASWdFSZT7RFeWhrDypgu6WhN
      MeaPJWeEFuRgyd/CfAANF7Blq29zbgAAAAAElFTkSuQmCC`;
    } else if (type === 'INDIVIDUAL') {
      icon = `data:image/png;base64,
      iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAYAAADimHc4AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAACypJ
      REFUeNrsXQuMVtURvvdfVhDB1VYXhMVHMRFRUUDLSmyRkGjtCihKbaKoGF8xxURDsCVqYgVEWswqiY/Iy1Q0onHRRV0fYNL6AEtF
      AVEMLgpFYHmUt8Iu+3fGnU1+f2bOPff+c+69/+5O8uUm97/3zJyZc89jzsz5fS+llM1mz4XLlYCLAX0B5YDugFJAifDaEUAjYB+g
      AfAN4GPAYt/313gdZFT4QMBcwAZAY1afGqls5DGwQ+MtSh8GqAMcyMZPB4j3sPam9C6AasCubHpoF8l0bFtWfA9ALaApm15qIhl7
      xKUXPwbFnwCXeYDREfk1A/YAtgE2ArYCttA1l3oCTqHrqQBUYhkgE0VswGuA8TB47y7mVj8jwoD6A2AF4FHAAAUZBlBZK6jssAP3
      jGJUfCWgIURF9wEWAS6KQbaLiNe+EPJhXSqLRfk4zWu2qBQ+sxIwMkFZR5IMtvLOS7PiewP+a1kRnP71SZHsfUgmG0NgHXunTfmj
      AYcthF8a5wwj4kxtqUU9sK6j0yL0ZIuWswkwuIjGsMEkc9CXPDlpQecECHkEMKWIZ3FTqA4mmpOUcC8FCLYN0E+JVwlgOGAa4C1A
      fc4sBq/fUh8+HXAZoFSxnv2oLiZ6KW3Kfw+QUeBzOmBmBLfFHsBTig0gQ3VK3ggW3c4UBR6/AMxSclssAFQodknJdUc04Jr6+3EK
      PKoA25V9PAcBdyrpYFzAuDDZlfJHGWY7KNCYAsv3qQ93Sa8CuiroYozBCM1hpqi+JUNceNQDjhGcZWN933+1gAp1gst8wPUBj/4P
      UAf4N8mzhxxupwHQjXE54OSAMlYAfg/ybi/UCHB5WXD24a7cGcBjs1brN61wxym0/OcCWu5/AGODZjc0W0L3wj8DylsNKFPqjsQV
      s6Zvx+WAO81Q/l7ArVFmVPDO1YDNhrI/AnR2PDDPK7TwSkO//56SM0yiNYC+BZZ/Eq0PJHpaqZGapqiVhRTcYFhkZRSUs0Mofzlt
      5Ggop1PA1PlapXWCtFhriFroDMOMp5+C0POF8r/QUn6eghYI/LbiukNpxSzNjGaELewEw07WVAVhBxpWsH09BwTlHgP4WOD7uOOF
      WmOoRgUP10heTSVBXxPKv9HxQrICsFtwL5+qxGOjULeaMD5xaeC9UEHA/kLZ7+OUNAZXyu0C/1mKrmxpgdbDpoBaaTNFScBq9dlC
      +PFglTDl7a7EQ9rUqQ168VjBAWZnPTu3coOLKW1IOa4RFHSLUvlSL4K67RKlddYpCXaJUP41MRughPYQwrXQcDyk9Ue16aVdQuvv
      oyTUNCEkpbMXMwHPhxlZDmmFJ9JGP/cV7JJeGCZYbKVipd9nyn/FS4AoYIuj3yryWCnwGBbmkxmpJIwvBENN8BIiYSV+n2L5I627
      dCFEfJ+iML0EYS5O0AC16g60o3lwje5A6++Z1pUpXLiNiqWKspwh3P/aS46+Yu5pr8SXMPe6tiaJtDrUpG7gEUVBuI2Sg77v70zQ
      APXMvV7KPCTXzYRcAwxnHvgRlLNMURBuAyTp0G+uiz1OkwHoEHfvfmR+Gp5rAC5q4AvlynJuhsaEDbCfueciS2Ytc+8nnWcoG7GT
      Zd9VCB1m7nVJ2ACc6+GgAz7vMvdwn+Jc/AKqhJcWKAuxl7l3fMIGON6yWyqUXhDuV6EBhjI/NEPftUpZiK3c567lBo5I3IxnuzYT
      0mUz89NQNMCvmB/2OKisNN08J0EDcDt76xzx4nTaFw3AeTm3OWgF2AVxGzqJ5OmiQ85rycLPpy8dseR0Wp4RBqKNjoT4gLl3eUKt
      H/MVuG3CZY74cY2vOxqg1LK/djUbuABaYxLd0B+Ye+giWO6I3xbmXikaoMTyYQ2qEwajW2PufnDafQM39Yau0tXa5HvmXkkmRH+l
      MQ5sISPk020YJxSjDW4Qxr5/OOTJ6lQyQNahIHOE5f+DMbV+7HLvZ37CjZJaL2bKePHT68JM4y6NqAsLuk+Y/8+CL/RQWgzgLDwE
      KtkEl79y/SFgrkb8vqH1nyO0fhx8qx3r2g9jANe5vAu9lhj/fDoPMNuR8tHtgDkM3P7zozEcylEuCcaFoTwXQ198niH8cboyr66G
      WJ21GLYYQ325HIgmj/Kn8untmAbE+w1Ry9M1IuVI+XWG84GGxlTXt7ncNU9IiPsyJqFwo/51gxFmk8sgavllgH8Zyr83xrXHWob/
      do/SdexiVxz1zYB1goKwcfyygLIrDSHjNV6MJMRcrcZBeD3zfFlMQnWDyy2GQf+uQvaMaUtVis0fgqmrxlBBXeJ0uh6V8GehhQxw
      qPgTAQ8aMmSQXtZaeAE+NfDZApikFZgryCAFgf0Ffzxb+PERB4KcAvgbYH9AFuPWQroehu/ZFseV7aYEi5Mc1FtKROzX+gA3HVyh
      KMCZmBBHsZc2dJUDJUwIcWbdLM2dOjqv7qismdwH6jlBlOb6L4Q482EndgcOZ1xTLb6+3LQinLv3V+DNfX0bch+YLQjx64gMhxgS
      PaR+eCINyq4Hfsx/eyBg/MmPDl8U9bhj9G9JU+zchwZpTNVQgRZZ77m0HnBHHCtRYYF2tyGnizPEE2FlpfMpOBqU/yD3ae4Nqfzl
      lpX5HPBH2hhJlCh78mZhocTR4jCLQ0p9yqf93INvCgyrLBnNtxAejwe4Mo5kvAiGyNApKJ9Y1OMhyzKrhPff5B6W0oc+tWA0KEDg
      ojqlHGQdEXD8AKa19rIoR1p//EZ6YafQ91UEMHpaYPRutojP6qdTdiUv6gMB71YIKUo7TS/NtP5kfv7eOuGM0BKvyCnbctw+V78P
      A96TuvTHgpg1CudD9Ag5z73OayMEdflTGIclpakesUlTzeQ5rzCOfTFTJj73okFGzqG1zWs7xGXSnGh4/kWP322sJR0brV1ucOFe
      KLzD0aVt6Au4lKug8Oxgwykz5VzLznfh4vk2iwRZarwOCiJJd4tIt2YDEI0HNDH3cWSfZilI5/ameTrKh5sxNpFOwxVm+JTOynv2
      cBwezYTXBcYuCHVi6LqnRmUsHsOVe2SZcAZPWzLAVSYD0CpaPN7NVHZQZBxmyXODDaacvtPO+/rchIt3PD4NN0s6jGYAGDQ+gcuz
      ws8jQowHbbnfRx2MEH6eQzosmMl3Br/Ize20C8I632TQy3c2ZdsG5w7x+DRTpLkef8xBWyess3SuxGHSmY4B4DPCjJkxwniAruXS
      dmiAUo8PuEUdjSGdqX0BaIQ34DLJ66AgmkS68lQNQEb4O1ye6dCxSM+QjjwnBiAj4B8h2GTRd2lDirXZwVtAuoltZvB8wC7YZ4CT
      i13zWAchrieXnk9KuCctwk1GFbHyR1EdTPRk0kJOzAb/kdvCbNr+/s9cp94W/xSFdZ6YFoGvsAg7PEABud1SrPhuJGPQX6xjXa9I
      m/C9Av6xopV2UER2WYpkLyOZbKLlNttERCRVEd9w8jp3VnO1RuxlAfL2Jxn2Wspck8aYJq5iN9Ef79jSMsA9WaU/XAuQrYJ4LQsh
      H9ZlvFdsRJFmG0L+19cqCo/BP+HpqSBDTyprpnBquok2uD7XOo7z+nFBhgsUTJCOknSB+6irvZYjJjGkG0+02uEdfeIiHj2DyRW4
      9sAzSvEgKsw7Lo/AE4OncBfrqcAohiL6GrrRlHVTNr20Ka4w+SQNgacFXgdYkiLFLyGZYvfq+gkbA7sJPDhpLGBQzOwx6BgTARdC
      N1OfZidTbOsIuPzOa9new+z105VZfAv4yGs5D7UOlP59sXj5kjIIxqKeD8CDZc8kg/SmgRYH8/y4o0M0eOIAvZkUjjnQawCfg8JT
      GSr5fwEGAL8mNe3TED36AAAAAElFTkSuQmCC`;
      bgColor = 'linear-gradient(to right bottom, #1bb196, #43ba88, #67c177, #8ac665, #aeca54)';
    }

    if (disable) {
      icon = '';
      if (this.currentTheme === 'theme_dark') {
        bgColor = 'linear-gradient(to right bottom, #2e2e2e, #3c3c3c, #4b4b4b, #5a5a5a, #6a6a6a)';
      } else if (this.currentTheme === 'theme_light') {
        bgColor = 'linear-gradient(to right bottom, #616161, #757575, #9E9E9E, #BDBDBD, #E0E0E0)';
      }
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" width="30" height="30">
      <foreignObject x="0" y="0" width="30" height="30">
      <div xmlns="http://www.w3.org/1999/xhtml"
        style="
        display:flex;
        width:30px;
        height:30px;
        justify-content: center;
        align-items: center;
        background-image: ${bgColor};">
        <img height="20px" src="${icon}"/>
        </div>
      </foreignObject>
      </svg>`;

    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }

  /**
   * @method initNetwork
   * Initial VisJS network object
   */
  initNetwork() {
    this.nodeDataArray.forEach(el => {
      el.added = true;
    });
    this.linkDataArray.forEach(el => {
      el.added = true;
    });
    this.nodes = new vis.DataSet(this.nodeDataArray);
    this.edges = new vis.DataSet(this.linkDataArray);
    const container = document.getElementById('relationship-map');
    const data = {
      nodes: this.nodes,
      edges: this.edges
    };
    const options = {
      interaction: {
        navigationButtons: true,
        tooltipDelay: 100
      },
      physics: {
        enabled: true,
        solver: 'barnesHut',
        stabilization: true,
        barnesHut: {
          gravitationalConstant: -40000,
          centralGravity: 3,
          springLength: 300,
          springConstant: 0.09,
          damping: 0.9,
          avoidOverlap: 0.4
        }
      },
      layout: {
        randomSeed: 2
      },
      edges: {
        ...this.stateConfigs[this.currentTheme].edges.enable,
        smooth: {
          enabled: true,
          type: 'dynamic',
          forceDirection: false,
          roundness: 0.5
        }
      },
      nodes: {
        borderWidth: 1,
        size: 25,
        shadow: true,
        ...this.stateConfigs[this.currentTheme].nodes.enable
      }
    };
    const network = new vis.Network(container, data, options);
    this.ngZone.run(() => {
      network.on('click', this.onNodeClick.bind(this, network, options));
    });
    return network;
  }

  /**
   * @method onNodeClick
   * The function will be triggered if user click on the node in the map
   */
  onNodeClick(network: vis.Network, options: any, properties: any): void {
    if (properties.nodes.length === 0) {
      const enableUpdates = [];
      this.nodes.get().forEach((nodeData) => {
        enableUpdates.push({
          id: nodeData.id,
          image: this.generateNodeImage(nodeData.label, nodeData.type),
          ...this.stateConfigs[this.currentTheme].nodes.enable
        });
      });
      this.nodes.update(enableUpdates);
    } else {
      const ids = properties.nodes;
      const clickedNodes = this.nodes.get(ids);
      const node = clickedNodes[0];
      let nodeIds: vis.IdType[] = network.getSelectedNodes();
      let edgeIds: vis.IdType[] = network.getSelectedEdges();
      nodeIds = nodeIds.concat(this.linkDataArray.filter((edge: Edge) => edgeIds.indexOf(edge.id) >= 0).map((edge: Edge) => edge.to));
      nodeIds = nodeIds.concat(this.linkDataArray.filter((edge: Edge) => edgeIds.indexOf(edge.id) >= 0).map((edge: Edge) => edge.from));
      const updates = [];
      this.nodes.get().forEach((nodeData) => {
        updates.push({
          id: nodeData.id,
          ...this.stateConfigs[this.currentTheme].nodes.disable,
          image: this.generateNodeImage(nodeData.label, nodeData.type, true)
        });
      });
      this.nodes.get({
        filter: (item) => nodeIds.indexOf(item.id) >= 0
      }).forEach((nodeData) => {
        updates.push({
          id: nodeData.id,
          image: this.generateNodeImage(nodeData.label, nodeData.type),
          ...this.stateConfigs[this.currentTheme].nodes.enable
        });
      });
      this.nodes.update(updates);
      network.setSelection({
        nodes: nodeIds,
        edges: edgeIds
      }, {
        highlightEdges: false
      });
      if (node.loaded) { return; }
      this.GetCrpAndSharedCrpsByRecordId(node.id)
        .subscribe(([results, relatedCrps]: [Crp[], Crp[]]) => {

          const newNodes: Node[] = [];
          const newEdges: Edge[] = [];
          const existingNodeIds: number[] = [];

          results = results.filter(i => !i.primary);
          if (results.length > 0) {
            for (const crp of results) {
              const found = this.nodeDataArray.find(i => i.id === crp.record);
              if (!found && !newNodes.find(newNode => newNode.id === crp.record)) {
                newNodes.push({
                  ...options.nodes,
                  customer: node.customer,
                  type: crp.recordType,
                  id: crp.record,
                  shape: 'circularImage',
                  widthConstraint: 300,
                  image: this.generateNodeImage(crp.name, crp.recordType),
                  label: crp.name,
                });
              } else {
                existingNodeIds.push(crp.record);
              }
              const foundEdge = this.linkDataArray.find(i => i.from === node.id && i.to === crp.record);
              if (!foundEdge && !newEdges.find(newEdge => newEdge.from === node.id && newEdge.to === crp.record)) {
                const roles = crp.roles ? crp.roles.map(dataRoles => dataRoles.role).join('\r\n') : null;
                newEdges.push({
                  from: node.id,
                  title: roles || 'NOT DEFINED',
                  to: crp.record,
                  font: { align: 'top' },
                  arrows: 'to',
                  dashes: crp.status === 'CURRENT' ? false : true
                });
              }
            }
          }

          if (relatedCrps.length > 0) {
            for (const crp of relatedCrps) {
              const found = this.nodeDataArray.find(i => i.id === crp.record);
              if (!found) {
                newNodes.push({
                  ...options.nodes,
                  customer: node.customer,
                  id: crp.record,
                  type: crp.recordType,
                  shape: 'circularImage',
                  widthConstraint: 300,
                  image: this.generateNodeImage(crp.name, crp.recordType),
                  label: crp.name,
                  physics: true,
                });
              } else {
                existingNodeIds.push(crp.record);
              }
              const foundEdge = this.linkDataArray.find(i => i.from === crp.record && i.to === node.id);
              if (!foundEdge) {
                const roles = crp.roles.map(dataRoles => dataRoles.role).join('\r\n');
                relatedCrps.forEach(e => {
                  if (e.customer !== +node.customer) {
                    newEdges.push({
                      from: crp.record,
                      title: roles || 'NOT DEFINED',
                      to: node.id,
                      font: { align: 'top' },
                      arrows: 'to',
                      dashes: e.status === 'CURRENT' ? false : true
                    });
                  }
                });
              }
            }
          }
          nodeIds = _.uniq(nodeIds);
          edgeIds = _.uniq(edgeIds);
          newNodes.forEach(element => {
            if (nodeIds.indexOf(element.id) < 0) {
              this.nodes.add(element);
              nodeIds.push(element.id);
              this.nodeDataArray.push(element);
            }
          });
          newEdges.forEach(element => {
            this.edges.add(element);
            edgeIds.push(element.id);
            this.linkDataArray.push(element);
          });
          nodeIds.concat(existingNodeIds);
          nodeIds = _.uniq(nodeIds);
          this.nodes.update([{
            ...node,
            loaded: true
          }]);
          network.setSelection({
            nodes: nodeIds,
            edges: edgeIds
          }, {
            highlightEdges: false
          });
          nodeIds = nodeIds.concat(this.linkDataArray.filter(n => edgeIds.indexOf(n.id) >= 0).map(d => d.to));
          nodeIds = nodeIds.concat(this.linkDataArray.filter(n => edgeIds.indexOf(n.id) >= 0).map(d => d.from));
          nodeIds = _.uniq(nodeIds);
          const newNodeUpdates = [];
          this.nodes.get({
            filter: (item) => nodeIds.indexOf(item.id) >= 0
          }).forEach((nodeData) => {
            newNodeUpdates.push({
              id: nodeData.id,
              image: this.generateNodeImage(nodeData.label, nodeData.type),
              ...this.stateConfigs[this.currentTheme].nodes.enable
            });
          });
          this.nodes.update(newNodeUpdates);
        });
    }
  }

  /**
   * @method GetCrpAndSharedCrpsByRecordId
   * List all crps under record ID as well as the crps share the same record ID
   */
  GetCrpAndSharedCrpsByRecordId(recordId: number, all: boolean = false): Observable<[Crp[], Crp[]]> {
    return this.customerService.getRelatedCrp(recordId)
      .pipe(
        concatMap(({ results }: RecordCrpResponse) => {
          this.recordTracker[recordId] = results;
          const crps = results.filter(i => all || i.primary);
          let crps$: Observable<Crp[]> = of([]);
          let relatedCrps$: Observable<Crp[]> = of([]);
          if (results.filter(i => !i.primary).length > 0) {
            relatedCrps$ = forkJoin(results.filter(i => !i.primary)
              .map((relatedCrp: RecordCrp) => {
                return this.customerService.getCustomerCrps({
                  customerId: relatedCrp.customer.id
                }).pipe(
                  map((result: CrpResponse) => {
                    const found: Crp = result.results.find((c: Crp) => c.primary);
                    const foundRoles = result.results.find((i: Crp) => i.record === relatedCrp.record);
                    // override the role for the recordId under the relatedCrp
                    // We are not get the role of this related but the role of the record that belong to this CRP
                    if (found) {
                      found.roles = foundRoles.roles || [];
                    }
                    return found;
                  })
                );
              })
            );
          }
          if (crps.length > 0) {
            const observables = crps.map((crp: RecordCrp) => {
              return this.customerService.getCustomerCrps({
                customerId: crp.customer.id
              }).pipe(
                map((crpResponse: CrpResponse) => crpResponse.results),
              );
            });
            crps$ = forkJoin(observables).pipe(
              map((concatCrps: Crp[][]) => concatCrps.reduce((crp: Crp[], crp2: Crp[]) => [...crp, ...crp2])),
            );
          }
          return zip(crps$, relatedCrps$);
        })
      );
  }

  /**
   * @method GetCrpAndSharedCrpsByCustomerId
    * List all crps under Customer ID as well as the crps share the same Customer ID
   */
  GetCrpAndSharedCrpsByCustomerId(customerId: number): Observable<[Crp[], Crp[]]> {
    return this.customerService.getCustomerCrps({
      customerId: customerId,
      offset: 0,
      limit: 9999,
      sort: null
    }).pipe(
      concatMap((crps: CrpResponse) => this.GetCrpAndSharedCrpsByRecordId(crps.results.find((crp: Crp) => crp.primary).record)));
  }

  /**
   * @method loadNetworkData
   * Recursive function to load the relationship map, by default is 1 layer from starting point
   */
  loadNetworkData(customerId: number, primaryRecord: number = null, layer: number = 1, limit: number = 100) {
    if (layer === 0 || limit === 0) {
      return of(null);
    }
    layer--;
    let sub: Observable<[Crp[], Crp[]]>;
    if (this.customerTracker[customerId] && this.sharedCrpTracker[customerId]) {
      sub = zip(of(this.customerTracker[customerId]), of(this.sharedCrpTracker[customerId]));
    } else {
      sub = this.GetCrpAndSharedCrpsByCustomerId(customerId);
      limit--;
    }

    return sub.pipe(
      switchMap(([crps, relatedCrps]: [Crp[], Crp[]]) => {
        const newNodes: Node[] = [];
        const newEdges: Edge[] = [];
        this.customerTracker[customerId] = crps;
        this.sharedCrpTracker[customerId] = relatedCrps;
        const listCrp: Crp[] = crps;
        const subscirbes: Array<Observable<RecordCrp[]>> = [];
        if (!listCrp
          || listCrp.length === 0
          || (listCrp.length === 1
            && listCrp[0].customer === customerId
          )) {
          return of(null);
        }
        // Add CRP under this record to the network
        listCrp.forEach(data => {
          const index = this.nodeDataArray.findIndex((node) => node.customer === customerId && node.id === data.record);
          if (index < 0) {
            if (data.primary) {
              primaryRecord = data.record;
            }
            const found = this.nodeDataArray.find(i => i.id === data.record);
            if (primaryRecord !== data.record) {
              const foundEdge = this.linkDataArray.find(i => i.from === primaryRecord && i.to === data.record);
              const roles = data.roles.map(dataRoles => dataRoles.role).join('\r\n');
              if (!found) {
                const newNode: Node = this.generateNode({
                  customerId: customerId,
                  recordId: data.record,
                  type: data.recordType,
                  name: data.name,
                  disable: false
                });
                newNodes.push(newNode);
                this.nodeDataArray.push(newNode);
              }
              if (!foundEdge) {
                const newEdge: Edge = {
                  from: primaryRecord,
                  title: roles || 'NOT DEFINED',
                  to: data.record,
                  font: { align: 'top' },
                  arrows: 'to',
                  dashes: data.status === 'CURRENT' ? false : true
                };
                newEdges.push(newEdge);
                this.linkDataArray.push(newEdge);
              }
            } else {
              if (!found) {
                const newNode: Node = this.generateNode({
                  customerId: customerId,
                  recordId: data.record,
                  type: customerId === this.customerId ? 'SELF' : data.recordType,
                  name: data.name,
                  disable: false
                });
                newNodes.push(newNode);
                this.nodeDataArray.push(newNode);
              }
            }
            if (this.recordTracker[data.record]) {
              subscirbes.push(of(this.recordTracker[data.record]));
            } else {
              limit -= 1;
              subscirbes.push(this.customerService.getRelatedCrp(data.record).pipe(
                map((recordCrpResult: RecordCrpResponse) => recordCrpResult.results)
              ));
            }
          }
        });
        // Add Crp that links to this record to the network
        relatedCrps.forEach((data: Crp) => {
          const index = this.nodeDataArray.findIndex((node) => node.id === data.record);
          if (index < 0) {
            const roles = data.roles.map(dataRoles => dataRoles.role).join('\r\n');
            const newNode: Node = this.generateNode({
              customerId: customerId,
              recordId: data.record,
              type: data.recordType,
              name: data.name,
              disable: false
            });
            const newEdge: Edge = {
              from: data.record,
              title: roles || 'NOT DEFINED',
              to: primaryRecord,
              font: { align: 'top' },
              arrows: 'to'
            };
            this.linkDataArray.push(newEdge);
            this.nodeDataArray.push(newNode);

            newEdges.push(newEdge);
            newNodes.push(newNode);
          }
          if (this.recordTracker[data.record]) {
            subscirbes.push(of(this.recordTracker[data.record]));
          } else {
            subscirbes.push(this.customerService.getRelatedCrp(data.record).pipe(
              map((recordCrpResult: RecordCrpResponse) => recordCrpResult.results)
            ));
            limit -= 1;
          }
        });
        if (newNodes.length > 0 || newEdges.length > 0) {
          this.draw$.next({ nodes: newNodes, edges: newEdges });
        }
        if (subscirbes.length > 0) {
          return zip(...subscirbes);
        } else {
          return of(null);
        }
      }),
      switchMap((results: RecordCrp[][]) => {
        // This is recursive process to continue to loadNetworkData for all customers have been found from above
        if (!results) { return of(null); }
        const recursives = [];
        results.forEach((crps: RecordCrp[]) => {
          this.recordTracker[crps[0].record] = crps;

          crps.forEach((crp: RecordCrp) => {
            const func = this.loadNetworkData(crp.customer.id, primaryRecord, layer, limit);
            recursives.push(func);
          });
        });
        if (recursives.length > 0) {
          return zip(...recursives);
        } else {
          return of(null);
        }
      })
    );
  }

  /**
   * @method toggleFullScreen
   * Toggle full screen when user click on the control buttons on UI
   */
  toggleFullScreen() {
    this.loading = true;
    interval(1000).pipe(
      take(1)
    ).subscribe(() => {
      this.loading = false;
      this.cdRef.detectChanges();
    });
    this.maximized = !this.maximized;
    if (this.maximized) {
      this.network.setSize('100%', `${window.innerHeight}`);
    } else {
      this.network.setSize('100%', this.defaultCanvasHeight);
    }
    this.network.redraw();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
